/**
 * LogoUpload Component
 *
 * Allows tenant admins to upload, preview, and delete their company logo.
 * Supports PNG, JPG, JPEG, WEBP, and SVG formats with max 2MB file size.
 */

import { useState, useRef, useCallback } from 'react';
import { PhotoIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { tenantService } from '../../api/tenant.service';
import type { Tenant } from '../../types/tenant.types';

// ============================================================================
// CONSTANTS
// ============================================================================

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

// ============================================================================
// TYPES
// ============================================================================

interface LogoUploadProps {
  tenant: Tenant;
  onLogoUpdated: (tenant: Tenant) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const LogoUpload: React.FC<LogoUploadProps> = ({ tenant, onLogoUpdated }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file before upload
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload PNG, JPG, WEBP, or SVG.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 2MB.';
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsUploading(true);

      try {
        const updatedTenant = await tenantService.uploadLogo(tenant.tenantId, file);
        onLogoUpdated(updatedTenant);
      } catch (err) {
        console.error('Failed to upload logo:', err);
        setError(err instanceof Error ? err.message : 'Failed to upload logo. Please try again.');
      } finally {
        setIsUploading(false);
      }
    },
    [tenant.tenantId, onLogoUpdated]
  );

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!tenant.logoUrl) return;

    setError(null);
    setIsDeleting(true);

    try {
      const updatedTenant = await tenantService.deleteLogo(tenant.tenantId);
      onLogoUpdated(updatedTenant);
    } catch (err) {
      console.error('Failed to delete logo:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete logo. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Click to upload
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-6">
        {/* Logo Preview / Upload Zone */}
        <div
          className={`
            relative flex items-center justify-center
            w-32 h-32 rounded-lg border-2 border-dashed
            transition-colors duration-200 cursor-pointer
            ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={isUploading ? undefined : handleClick}
        >
          {tenant.logoUrl ? (
            <img
              src={tenant.logoUrl}
              alt={`${tenant.tenantName} logo`}
              className="max-w-full max-h-full object-contain rounded-md"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <PhotoIcon className="w-10 h-10 mb-1" />
              <span className="text-xs">No logo</span>
            </div>
          )}

          {/* Upload overlay */}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
              <svg
                className="animate-spin h-8 w-8 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleInputChange}
            className="hidden"
            disabled={isUploading}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
            onClick={handleClick}
            disabled={isUploading || isDeleting}
            loading={isUploading}
          >
            {tenant.logoUrl ? 'Change Logo' : 'Upload Logo'}
          </Button>

          {tenant.logoUrl && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<TrashIcon className="w-4 h-4" />}
              onClick={handleDelete}
              disabled={isUploading || isDeleting}
              loading={isDeleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Remove Logo
            </Button>
          )}

          <p className="text-xs text-gray-500 mt-1">
            PNG, JPG, WEBP, or SVG. Max 2MB.
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default LogoUpload;
